import React from 'react';
import {Box, Paper} from '@mui/material';
import {AnyQuestion, Form} from 'crqlar-react-common/dist/models/forms/form';
import QuestionEditor from './questionEditors/QuestionEditor';
import FormThemeProvider from '../FormThemeProvider';

interface ScreenEditorProps {
    form: Form,
    question: AnyQuestion;
    onQuestionUpdate: (question: AnyQuestion) => void;
}

const ScreenEditor: React.FC<ScreenEditorProps> = ({
                                                       form,
                                                       question,
                                                       onQuestionUpdate
                                                   }) => {
    // Merge question theme with form theme, question takes precedence
    const mergedTheme = question.theme ? {
        ...form.theme,
        palette: {
            ...form.theme?.palette,
            ...question.theme.palette
        },
        typography: {
            ...form.theme?.typography,
            ...question.theme.typography
        },
        shape: {
            ...form.theme?.shape,
            ...question.theme.shape
        },
        backgroundMedia: question.theme.backgroundMedia || form.theme?.backgroundMedia
    } : form.theme;

    const backgroundImageUrl = mergedTheme?.backgroundMedia?.mediaURI;
    const layout = question.layout || 'DEFAULT';
    const isSplitLayout = layout !== 'DEFAULT';

    // Determine flex direction and sizes based on layout
    const getLayoutStyles = () => {
        switch (layout) {
            case 'TOP_SPLIT':
                return {
                    flexDirection: 'column' as const,
                    imageSize: '50%',
                    contentSize: '50%'
                };
            case 'LEFT_SPLIT':
                return {
                    flexDirection: 'row' as const,
                    imageSize: '50%',
                    contentSize: '50%'
                };
            case 'RIGHT_SPLIT':
                return {
                    flexDirection: 'row-reverse' as const,
                    imageSize: '50%',
                    contentSize: '50%'
                };
            default:
                return {
                    flexDirection: 'column' as const,
                    imageSize: '0%',
                    contentSize: '100%'
                };
        }
    };

    const layoutStyles = getLayoutStyles();

    return (
        <FormThemeProvider theme={mergedTheme}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    maxHeight: 1000,
                    position: 'relative',
                    padding: 3,
                    backgroundColor: 'grey.100',
                }}
            >
                {/* Screen Content Area */}
                <Paper
                    elevation={1}
                    sx={{
                        maxWidth: 800,
                        width: '100%',
                        mx: 'auto',
                        borderRadius: 2,
                        overflow: 'hidden',
                        flex: 1,
                        display: 'flex',
                        flexDirection: layoutStyles.flexDirection,
                        position: 'relative',
                    }}
                >
                    {/* Split Layout: Image Section */}
                    {isSplitLayout && backgroundImageUrl && (
                        <Box
                            sx={{
                                width: layout === 'TOP_SPLIT' ? '100%' : layoutStyles.imageSize,
                                height: layout === 'TOP_SPLIT' ? layoutStyles.imageSize : '100%',
                                backgroundImage: `url(${backgroundImageUrl})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                            }}
                        />
                    )}

                    {/* Content Section */}
                    <Box
                        sx={{
                            width: isSplitLayout && backgroundImageUrl ?
                                (layout === 'TOP_SPLIT' ? '100%' : layoutStyles.contentSize) :
                                '100%',
                            height: isSplitLayout && backgroundImageUrl && layout === 'TOP_SPLIT' ?
                                layoutStyles.contentSize :
                                '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: 4,
                            position: 'relative',
                        }}
                    >
                        {/* Background Image Layer (bottom) - Only for DEFAULT layout */}
                        {!isSplitLayout && backgroundImageUrl && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundImage: `url(${backgroundImageUrl})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                    zIndex: 0,
                                }}
                            />
                        )}

                        {/* Theme Background Layer (middle) */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'background.paper',
                                zIndex: 1,
                            }}
                        />

                        {/* Content Layer (top) */}
                        <Box sx={{ position: 'relative', zIndex: 2, width: '100%' }}>
                            <QuestionEditor
                                question={question}
                                onQuestionUpdate={onQuestionUpdate}
                            />
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </FormThemeProvider>
    );
};

export default ScreenEditor;